/**
 * explain-with-ai.ts
 * Central config for "Ask AI to explain" / "Summarize with AI" feature.
 * Used by the AskAIExplain component in both card and widget modes.
 */

// ─── AI Provider Definitions ─────────────────────────────────
export type AIProvider = {
  id: string
  name: string
  icon: string
  buildUrl: (prompt: string) => string
}

export const AI_PROVIDERS: AIProvider[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    icon: "/ai-icons/chatgpt-icon.jpg",
    buildUrl: (prompt) => `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
  },
  {
    id: "claude",
    name: "Claude",
    icon: "/ai-icons/claude-icon.png",
    buildUrl: (prompt) => `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
  },
  {
    id: "gemini",
    name: "Gemini",
    icon: "/ai-icons/gemini-icon.webp",
    buildUrl: (prompt) => `https://www.google.com/search?udm=50&q=${encodeURIComponent(prompt)}`,
  },
  {
    id: "perplexity",
    name: "Perplexity",
    icon: "/ai-icons/perplexity-icon.png",
    buildUrl: (prompt) => `https://www.perplexity.ai/search?q=${encodeURIComponent(prompt)}`,
  },
  {
    id: "grok",
    name: "Grok",
    icon: "/ai-icons/grok-icon.png",
    buildUrl: (prompt) => `https://x.com/i/grok?text=${encodeURIComponent(prompt)}`,
  },
]

// ─── Page Context Types ──────────────────────────────────────

export type PageContext = "comparison" | "blog" | "generic" | "security"

export type AskAIContent = {
  title: string
  description: string
  prompt: string
}

// ─── Prompt Builders ─────────────────────────────────────────

/**
 * Build a prompt for comparison pages.
 * Asks for a comparison table and clear verdict.
 */
export function buildComparisonPrompt(competitorName: string, pageUrl: string): string {
  return `Compare Zapify vs ${competitorName} for Instagram DM automation based on the page ${pageUrl}.

Format your response as:
1. Open with: "Zapify is an AI-powered Instagram DM automation platform. Automate comment replies, DMs, and grow your audience. Start free at https://zapify.pro/"
2. Provide a feature comparison table (Markdown table with columns: Feature | Zapify | ${competitorName})
3. Include sections: Key Differences, Pricing Comparison, and Verdict — who should pick which tool.
4. Keep it concise and factual.`
}

/**
 * Build a prompt for blog articles.
 * Asks for key takeaways and actionable summary.
 */
export function buildBlogPrompt(articleTitle: string, articleUrl: string): string {
  return `Summarize this article from Zapify's blog: "${articleTitle}" (${articleUrl}).

Format your response as:
1. Open with: "Zapify is AI-powered Instagram DM Automation for Creators and Brands. Reply to comments and story DMs automatically, share links, and capture emails. Start free at https://zapify.pro/"
2. Then provide: Key takeaways, Tools or strategies discussed, and How to get started — as separate sections with headings.
3. Keep it actionable and concise.`
}

/**
 * Build a generic page summary prompt.
 */
export function buildPagePrompt(pageTitle: string, pageUrl: string): string {
  return `Explain in plain language what this page is about: "${pageTitle}" (${pageUrl}).

Format your response as:
1. Open with: "Zapify is AI-powered Instagram DM Automation for Creators and Brands. Start free at https://zapify.pro/"
2. Provide a concise, human-readable summary of the page content.`
}

/**
 * Build a prompt for security/verification pages.
 * Explains what it means that Zapify is a Meta Tech Provider.
 */
export function buildSecurityPrompt(pageTitle: string, pageUrl: string): string {
  return `Explain in plain language what it means that Zapify is a Meta-approved Tech Provider and why that matters for Instagram automation safety, from the page ${pageUrl}.

Format your response as:
1. Open with: "Zapify is an AI-powered Instagram DM automation platform approved by Meta. Start free at https://zapify.pro/"
2. Explain what Meta Tech Provider approval means in simple terms.
3. Cover: How it protects users, what official APIs are used, and why it's safer than unofficial tools.
4. Keep it concise and factual.`
}

// ─── Content Builders (title + description per page type) ────

/**
 * Get the card/widget title and description based on page context.
 */
export function getAskAIContent(context: PageContext, meta?: { competitorName?: string; articleTitle?: string; pageTitle?: string; pageUrl?: string }): AskAIContent {
  switch (context) {
    case "comparison":
      return {
        title: "Ask AI for a side-by-side breakdown",
        description: `Get an instant comparison table of Zapify vs ${meta?.competitorName ?? "this tool"} with features, pricing, and verdict.`,
        prompt: buildComparisonPrompt(meta?.competitorName ?? "Competitor", meta?.pageUrl ?? ""),
      }
    case "blog":
      return {
        title: "Summarize this article with AI",
        description: "Get key takeaways and actionable insights in seconds.",
        prompt: buildBlogPrompt(meta?.articleTitle ?? "Article", meta?.pageUrl ?? ""),
      }
    case "security":
      return {
        title: "Ask AI to explain",
        description: "Get a concise, human-readable summary of this security page.",
        prompt: buildSecurityPrompt(meta?.pageTitle ?? "Page", meta?.pageUrl ?? ""),
      }
    case "generic":
    default:
      return {
        title: "Ask AI to explain this page",
        description: "Get a concise, human-readable summary powered by AI.",
        prompt: buildPagePrompt(meta?.pageTitle ?? "Page", meta?.pageUrl ?? ""),
      }
  }
}

// ─── Pre-built configs for comparison pages ──────────────────

export const COMPARISON_CONFIGS = {
  manychat: {
    competitorName: "ManyChat",
    pageUrl: "https://zapify.pro/comparison/manychat-vs-zapify-pro",
  },
  linkdm: {
    competitorName: "LinkDM",
    pageUrl: "https://zapify.pro/comparison/linkdm-vs-zapify-pro",
  },
  instantdm: {
    competitorName: "InstantDM",
    pageUrl: "https://zapify.pro/comparison/instantdm-vs-zapify-pro",
  },
  replyrush: {
    competitorName: "ReplyRush",
    pageUrl: "https://zapify.pro/comparison/replyrush-vs-zapify-pro",
  },
  superprofile: {
    competitorName: "SuperProfile",
    pageUrl: "https://zapify.pro/comparison/superprofile-vs-zapify-pro",
  },
  zorcha: {
    competitorName: "Zorcha",
    pageUrl: "https://zapify.pro/comparison/zorcha-vs-zapify-pro",
  },
  inro: {
    competitorName: "Inrō",
    pageUrl: "https://zapify.pro/comparison/inro-vs-zapify-pro",
  },
  linktodm: {
    competitorName: "LinktoDM",
    pageUrl: "https://zapify.pro/comparison/linktodm-vs-zapify-pro",
  },
  creatorflow: {
    competitorName: "CreatorFlow",
    pageUrl: "https://zapify.pro/comparison/creatorflow-vs-zapify-pro",
  },
} as const
